import React, { useState, useEffect } from 'react';
import { placeOrder } from '../../services/websocketApi/orders';
import { getInstrumentDetails } from '../../services/validation';
import { formatNumber, formatPnL, formatSymbol } from '../../services/validation';
import { useUserDataContext } from '../../contexts/UserDataContext';
import './ClosePositionModal.css';

/**
 * ClosePositionModal component for handling position closing with slider functionality
 */
const ClosePositionModal = ({ 
  position, 
  onClose, 
  onPositionClosed, 
  isClosing 
}) => {
  const [closePercentage, setClosePercentage] = useState(100);
  const [closeQuantity, setCloseQuantity] = useState(0);
  const [instrumentDetails, setInstrumentDetails] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [minQuantity, setMinQuantity] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [dataLoadingFailed, setDataLoadingFailed] = useState(false);
  
  // Access UserDataContext to get cached instrument data
  const { instrumentsData } = useUserDataContext();

  // Fetch instrument details when position changes
  useEffect(() => {
    if (!position) return;
    
    setValidationError(null);
    setDataLoadingFailed(false);
    setIsDataLoading(true);
    
    // Log position details to help with debugging
    console.log("Position to close:", {
      symbol: position.symbol,
      quantity: position.quantity,
      side: position.side
    });
    
    // Start with default 100% closing
    setClosePercentage(100);
    setCloseQuantity(position.quantity);
    
    // Check if we already have the instrument data in the context
    const cachedInstrument = findInstrumentInCache(position.symbol);
    
    if (cachedInstrument) {
      console.log("Using cached instrument data:", cachedInstrument);
      processInstrumentData(cachedInstrument);
      setIsDataLoading(false);
    } else {
      // Fallback: Fetch instrument details if not in cache
      console.log("Instrument not found in cache, fetching from API:", position.symbol);
      
      getInstrumentDetails(position.symbol)
        .then(details => {
          setIsDataLoading(false);
          
          if (!details) {
            console.error(`No instrument details found for ${position.symbol}`);
            setValidationError('Unable to load required instrument details. Cannot close position.');
            setDataLoadingFailed(true);
            return;
          }
          
          processInstrumentData(details);
        })
        .catch(error => {
          setIsDataLoading(false);
          console.error(`Error fetching instrument details: ${error.message}`);
          setValidationError('Error loading instrument details. Cannot close position.');
          setDataLoadingFailed(true);
        });
    }
  }, [position, instrumentsData]);
  
  // Find the instrument in the cache (check different collections where it might be stored)
  const findInstrumentInCache = (symbol) => {
    if (!symbol) return null;
    
    // Check different possible locations where instrument data might be stored
    
    // Check in instruments array (primary location)
    if (Array.isArray(instrumentsData?.instruments)) {
      const match = instrumentsData.instruments.find(inst => inst.symbol === symbol);
      if (match) return match;
    }
    
    // Check in instrumentsById object (if exists)
    if (instrumentsData?.instrumentsById && instrumentsData.instrumentsById[symbol]) {
      return instrumentsData.instrumentsById[symbol];
    }
    
    // Check in instrumentsData directly (some implementations store flat)
    if (Array.isArray(instrumentsData)) {
      const match = instrumentsData.find(inst => inst.symbol === symbol);
      if (match) return match;
    }
    
    // Check in window cache as last resort
    if (window.__instrumentsData && Array.isArray(window.__instrumentsData)) {
      return window.__instrumentsData.find(item => item.symbol === symbol);
    }
    
    return null;
  };
  
  // Process the instrument data once we have it
  const processInstrumentData = (details) => {
    console.log("Processing instrument details:", {
      symbol: details.symbol,
      min_Order_Size: details.min_Order_Size,
      contract_Size: details.contract_Size
    });
    
    setInstrumentDetails(details);
    
    // Extract required properties directly
    const minOrderSize = details.min_Order_Size ? parseFloat(details.min_Order_Size) : null;
    const contractSize = details.contract_Size ? parseFloat(details.contract_Size) : null;
    
    // Calculate min display quantity
    if (minOrderSize !== null && contractSize !== null) {
      const displayMin = minOrderSize * contractSize;
      console.log("Calculated min quantity:", {
        minOrderSize,
        contractSize,
        displayMin
      });
      
      setMinQuantity(displayMin);
      
      // Now update the close quantity and percentage with proper rounding
      const { finalQuantity, actualPercentage } = calculateCloseUpdate(
        100, 
        position.quantity, 
        displayMin
      );
      setCloseQuantity(finalQuantity);
      setClosePercentage(actualPercentage);
      
    } else {
      console.error(`Missing min_Order_Size or contract_Size:`, {
        min_Order_Size: details.min_Order_Size,
        contract_Size: details.contract_Size
      });
      setValidationError('Missing required order size limits for this instrument. Cannot close position.');
      setDataLoadingFailed(true);
    }
  };

  // Function to calculate the appropriate close quantity and percentage based on rules
  const calculateCloseUpdate = (percentage, totalQuantity, minSize) => {
    let finalQuantity = 0;
    let actualPercentage = 0;

    const validMinSize = minSize && typeof minSize === 'number' && minSize > 0 ? minSize : null;

    if (validMinSize) {
      if (totalQuantity < validMinSize) {
        finalQuantity = (percentage > 0) ? totalQuantity : 0;
        console.log(`Total position (${totalQuantity}) is less than min size (${validMinSize}). Close qty forced to ${finalQuantity}.`);
      } else {
        if (percentage > 0) {
          const rawQuantity = totalQuantity * (percentage / 100);
          let calculatedQuantity = Math.floor(rawQuantity / validMinSize) * validMinSize;

          if (calculatedQuantity < validMinSize) {
            calculatedQuantity = validMinSize;
            console.log(`Calculated quantity was below minimum, bumping to min size: ${validMinSize}`);
          }

          finalQuantity = Math.min(calculatedQuantity, totalQuantity);

          console.log("Quantity calculation (valid total >= min):", {
            percentage,
            totalQuantity,
            rawQuantity,
            minSize: validMinSize,
            calculatedQuantityBeforeCap: calculatedQuantity,
            finalQuantity
          });
        } else {
          finalQuantity = 0;
          console.log("Percentage is 0, closing quantity set to 0.");
        }
      }
    } else {
      const rawQuantity = totalQuantity * (percentage / 100);
      finalQuantity = parseFloat(rawQuantity.toFixed(8));
      console.log("No valid minimum size available, using raw percentage calculation:", finalQuantity);
    }

    if (isNaN(finalQuantity) || finalQuantity < 0) {
      console.warn(`Calculated final quantity was invalid (${finalQuantity}), resetting to 0.`);
      finalQuantity = 0;
    }

    // Calculate the actual percentage corresponding to the final quantity
    if (totalQuantity > 0) {
      actualPercentage = Math.round((finalQuantity / totalQuantity) * 100);
    } else {
      actualPercentage = 0;
    }

    // Return the calculated values instead of setting state here
    return { finalQuantity, actualPercentage };
  };

  // Update state based on slider interaction
  const handleSliderChange = (e) => {
    if (!position || dataLoadingFailed) return;

    const requestedPercentage = parseInt(e.target.value);
    const { finalQuantity, actualPercentage } = calculateCloseUpdate(
      requestedPercentage, 
      position.quantity, 
      minQuantity
    );

    setCloseQuantity(finalQuantity);
    setClosePercentage(actualPercentage); // Update percentage state to reflect actual value
  };

  // Set close percentage to a specific value and update state
  const setQuickPercentage = (percentage) => {
    if (!position || dataLoadingFailed) return;

    const { finalQuantity, actualPercentage } = calculateCloseUpdate(
      percentage, 
      position.quantity, 
      minQuantity
    );

    setCloseQuantity(finalQuantity);
    setClosePercentage(actualPercentage); // Update percentage state to reflect actual value
  };
  
  // Confirm close position
  const confirmClosePosition = () => {
    if (!position || closeQuantity <= 0 || isClosing || dataLoadingFailed) return;
    
    // Clear previous errors
    setValidationError(null);
    
    // Basic validations
    if (minQuantity && closeQuantity < minQuantity) {
      setValidationError(`Quantity must be at least ${minQuantity}`);
      return;
    }
    
    if (closeQuantity > position.quantity) {
      setValidationError(`Cannot close more than the position size (${position.quantity})`);
      return;
    }
    
    // Create order params 
    const orderParams = {
      symbol: position.symbol,
      side: position.side === 'buy' ? 'sell' : 'buy',
      orderType: 'market',
      price: position.markPrice || position.entryPrice,
      quantity: closeQuantity,
      leverage: position.leverage,
      mode: "close"
    };
    
    console.log("Sending close position order:", orderParams);
    
    // Execute order via callback to parent
    onPositionClosed(orderParams);
  };

  // Get theme color based on position side
  const getThemeColor = () => {
    return position.side === 'buy' ? '#18aa7a' : '#ef4444';
  };

  if (!position) return null;

  return (
    <div className="close-modal-overlay">
      <div className="close-modal">
        <div className="close-modal-header">
          <h3>Close Position</h3>
          <button className="btn-close-modal" onClick={onClose}>×</button>
        </div>
        <div className="close-modal-content">
          <div className="position-summary">
            <div>
              <span className={`position-badge ${position.side === 'buy' ? 'long' : 'short'}`}>
                {position.side === 'buy' ? 'Long' : 'Short'} {formatSymbol(position.symbol)}
              </span>
            </div>
            <div className="position-size">
              {formatNumber(position.quantity, 8)}
            </div>
          </div>
          
          {isDataLoading ? (
            <div className="data-loading">
              Loading instrument details...
            </div>
          ) : dataLoadingFailed ? (
            <div className="data-error">
              {validationError || 'Failed to load necessary data. Cannot close position.'}
            </div>
          ) : (
            <div className="close-amount-control">
              <div className="slider-header">
                <span>Close Amount</span>
                <span className="percentage">{closePercentage}%</span>
              </div>
              
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={closePercentage}
                  onChange={handleSliderChange}
                  className="position-slider"
                  style={{
                    '--percent': `${closePercentage}%`,
                    '--slider-active-color': getThemeColor()
                  }}
                />
                
                <div className="slider-labels">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
              
              <div className="quick-amounts">
                <button onClick={() => setQuickPercentage(25)}>25%</button>
                <button onClick={() => setQuickPercentage(50)}>50%</button>
                <button onClick={() => setQuickPercentage(75)}>75%</button>
                <button onClick={() => setQuickPercentage(100)}>100%</button>
              </div>
              
              <div className="close-size">
                Closing <span className="highlight">{formatNumber(closeQuantity, 8)}</span> of <span>{formatNumber(position.quantity, 8)}</span>
              </div>
              
              {validationError && !dataLoadingFailed && (
                <div className="validation-error">{validationError}</div>
              )}
              
              {minQuantity && (
                <div className="min-quantity-info">
                  Closing amount must be a multiple of {formatNumber(minQuantity, 8)}
                </div>
              )}
              
              <div className="position-info">
                <div>
                  <span>Entry Price</span>
                  <strong>{formatNumber(position.entryPrice, 5)}</strong>
                </div>
                <div>
                  <span>Mark Price</span>
                  <strong>{formatNumber(position.markPrice || position.entryPrice, 5)}</strong>
                </div>
                <div>
                  <span>Current PnL</span>
                  <strong className={formatPnL(position.pnl).isPositive ? 'positive' : 'negative'}>
                    {formatPnL(position.pnl).value}
                  </strong>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="close-modal-actions">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button 
            className="btn-confirm"
            onClick={confirmClosePosition}
            disabled={closeQuantity <= 0 || closePercentage <= 0 || isClosing || validationError || dataLoadingFailed || isDataLoading}
            style={{ 
              backgroundColor: position.side === 'buy' ? '#ef4444' : '#ef4444',
              cursor: (closeQuantity <= 0 || closePercentage <= 0 || isClosing || validationError || dataLoadingFailed || isDataLoading) ? 'not-allowed' : 'pointer'
            }}
          >
            {isClosing ? 'Closing...' : 'Close Position'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosePositionModal; 